import { useState } from 'preact/hooks';
import { authorColor } from './chorus';
import { apiFetch } from '../api/client';
import type { LiveChatMessage } from '../hooks/useThreadEvents';

/**
 * CAPTURE_FIRST_STORY_FLOW — the guest-facing live banter lane (doc 70 §6.4).
 *
 * A lightweight, ephemeral chat stream so link-joined PWA guests stay in the
 * moment alongside the iOS crew. Author-tinted, OurTake-voiced. Banter is NOT a
 * Take — it stays out of the captured Story (a message can be promoted to a
 * take separately). Posts to the v2 guestOrAuth route; every client (iOS crew +
 * other guests) renders the echoed `live:message` event.
 */
// Mirrors the backend cap (createLiveMessageSchema: content.max(500)). Enforced
// client-side so a long message can't fail server-side with no visible reason.
const MAX_LEN = 500;

interface Props {
  sessionId: string;
  messages: LiveChatMessage[];
}

export function LiveChatPanel({ sessionId, messages }: Props) {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    const text = draft.trim();
    if (!text || text.length > MAX_LEN || sending) return;
    setSending(true);
    setError(null);
    try {
      await apiFetch(`/live-sessions/${sessionId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ content: text }),
      });
      setDraft('');
    } catch {
      // Keep the draft so the guest can retry, but surface why nothing happened —
      // a silent failure (e.g. a server-side reject) otherwise looks like a no-op.
      setError("Couldn't send — please try again.");
    } finally {
      setSending(false);
    }
  };

  const canSend = draft.trim().length > 0 && draft.trim().length <= MAX_LEN && !sending;

  return (
    <section
      aria-label="Live chat"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--st-space-2)',
        padding: 'var(--st-space-3)',
        background: 'var(--st-surface-sunken)',
        borderRadius: 'var(--st-radius-md)',
        border: '1px solid var(--st-border-soft, rgba(0,0,0,0.08))',
      }}
    >
      <div style={{ fontSize: 'var(--st-text-sm)', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--st-text-secondary)' }}>
        {'Live chat'}
      </div>

      {messages.length === 0 ? (
        <p style={{ margin: 0, fontStyle: 'italic', color: 'var(--st-text-tertiary, var(--st-text-secondary))', fontSize: 'var(--st-text-sm)' }}>
          {'Say something to your crew…'}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--st-space-1, 4px)', maxHeight: '40vh', overflowY: 'auto' }}>
          {messages.map((m) => (
            <li key={m.id} style={{ display: 'flex', gap: 'var(--st-space-2)', alignItems: 'baseline' }}>
              <span style={{ fontWeight: 600, color: authorColor(m.colorSeed), whiteSpace: 'nowrap' }}>
                {m.authorDisplayName}
              </span>
              <span style={{ color: 'var(--st-text-primary)' }}>{m.content}</span>
            </li>
          ))}
        </ul>
      )}

      <div style={{ display: 'flex', gap: 'var(--st-space-2)', alignItems: 'center' }}>
        <input
          type="text"
          value={draft}
          maxLength={MAX_LEN}
          onInput={(e) => { setDraft((e.target as HTMLInputElement).value); if (error) setError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); send(); } }}
          placeholder={'Say something…'}
          aria-label="Say something"
          style={{
            flex: 1,
            padding: 'var(--st-space-2) var(--st-space-3)',
            borderRadius: 'var(--st-radius-full)',
            border: '1px solid var(--st-border-soft, rgba(0,0,0,0.12))',
            background: 'var(--st-surface-base)',
            color: 'var(--st-text-primary)',
            fontSize: 'var(--st-text-sm)',
          }}
        />
        <button
          type="button"
          onClick={send}
          disabled={!canSend}
          aria-label="Send message"
          style={{
            border: 'none',
            background: canSend ? 'var(--st-accent-warm)' : 'var(--st-surface-raised)',
            color: canSend ? '#fff' : 'var(--st-text-tertiary, var(--st-text-secondary))',
            borderRadius: 'var(--st-radius-full)',
            width: 40,
            height: 40,
            fontSize: 18,
            cursor: canSend ? 'pointer' : 'default',
          }}
        >
          {'↑'}
        </button>
      </div>

      {error && (
        <p role="alert" style={{ margin: 0, fontSize: 'var(--st-text-sm)', color: 'var(--st-danger, #b00020)' }}>
          {error}
        </p>
      )}
    </section>
  );
}
